import {
  MilesExpirationDate,
  PaymentMethod,
  sellSubmissionFormEventDataSchema,
} from "@/core";
import { formatPrice } from "@/lib";
import { MilesProgram } from "@/lib/miles-calculations";
import { cn } from "@/lib/utils";
import {
  useDashboardStore,
  useSecureQuoteStore,
  useUserCredentialsStore,
} from "@/stores";
import { Check } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { CheckoutButton } from "./CheckoutButton";
import { PayoutConfidenceSection } from "./PayoutConfidenceSection";
import { SellCredentialsSection } from "./SellCredentialsSection";
import { TermsAndConditions } from "./TermsAndConditions";

import {
  useHydrateSellFormContactDetails,
  useSellForm,
} from "@/hooks/forms/useSellForm";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { FloatingCTAButton } from "../dashboard";

export function DetailsSellForm({
  onContinueClick,
  programs,
  currentProgramIndex,
  isFormSubmitting,
  customerEmail,
  customerPhoneNumber,
}: Readonly<{
  programs: MilesProgram[];
  onContinueClick: () => void;
  currentProgramIndex: number;
  isFormSubmitting: boolean;
  customerEmail: string;
  customerPhoneNumber: string;
}>) {
  const transactionType = useUserCredentialsStore((s) => s.transactionType);
  const updateProgram = useDashboardStore((s) => s.updateProgram);

  const [isTermsAndConditionsOpen, setIsTermsAndConditionsOpen] =
    useState(false);
  // const [sameAsEmail, setSameAsEmail] = useState(true);
  const sameAsEmail = false;

  const formRef = useRef<HTMLDivElement>(null);

  const currentProgram = programs[currentProgramIndex];
  const accountDetails = currentProgram.accountDetails as Omit<
    z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
    "miles" | "averageRate" | "totalPayout"
  >;

  const saveDetails = useSecureQuoteStore((s) => s.saveDetails);
  const setSaveDetails = useSecureQuoteStore((s) => s.setSaveDetails);

  const form = useSellForm({
    frequentFlyerNumber: accountDetails.frequentFlyerNumber || "",
    accountPassword: accountDetails.accountPassword || "",
    accountEmail: accountDetails.accountEmail || customerEmail,
    accountPhoneNumber:
      accountDetails.accountPhoneNumber || customerPhoneNumber,
    milesExpirationDate:
      accountDetails.milesExpirationDate || MilesExpirationDate.THREE_MONTHS,
    paymentMethod: accountDetails.paymentMethod,
    paypalEmail: accountDetails.paypalEmail || "",
    notes: accountDetails.notes || "",
  });

  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    watch,
    setValue,
    trigger,
  } = form;

  const watchedPaymentMethod = watch("paymentMethod");
  const accountEmail = watch("accountEmail");

  useHydrateSellFormContactDetails({
    customerEmail,
    customerPhoneNumber,
    sameAsEmail,
    setValue,
  });

  const scrollToFirstError = useCallback(() => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        if (
          element instanceof HTMLElement &&
          typeof element.focus === "function"
        ) {
          setTimeout(() => element.focus(), 100);
        }
      }
    }
  }, [errors]);

  useEffect(() => {
    const subscription = watch((value) => {
      updateProgram({
        accountDetails: value as Omit<
          z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >,
      });
    });
    return () => subscription.unsubscribe();
  }, [
    watch,
    updateProgram,
    programs,
    currentProgramIndex,
    currentProgram?.programId,
  ]);

  // Keep PayPal email in sync with account email while "Same as email" is checked
  useEffect(() => {
    if (sameAsEmail && watchedPaymentMethod === PaymentMethod.PAYPAL) {
      setValue("paypalEmail", accountEmail || "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [sameAsEmail, accountEmail, watchedPaymentMethod, setValue]);

  const handleFormSubmit = () => {
    // Always close terms and conditions when submit is attempted
    //setIsTermsAndConditionsOpen(false);
  };

  const onSubmit = async () => {
    const isFormValid = await trigger();
    if (!isFormValid) {
      scrollToFirstError();
      return;
    }
    if (currentProgramIndex === programs.length - 1) {
      setSaveDetails(false);
      form.reset();
      onContinueClick();
      return;
    }

    if (saveDetails) {
      setValue("frequentFlyerNumber", "");
      setValue("notes", "");
      setValue("accountPassword", "");
    } else {
      form.reset();
    }
    onContinueClick();
  };
  return (
    <>
      <FloatingCTAButton offersRef={formRef} />
      <Card
        ref={formRef}
        className="w-full order-1 xl:order-2 bg-white rounded-md z-10 shadow-lg h-fit p-5 xl:p-7"
      >
        <Form {...form}>
          <form
            onSubmit={(e) => {
              handleFormSubmit();
              handleSubmit(onSubmit)(e);
            }}
            className="flex flex-col gap-4"
          >
            <FormField
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400 xl:text-sm xl:font-medium xl:normal-case xl:tracking-normal xl:text-gray-900">
                    How would you like to be paid?
                  </FormLabel>
                  <FormControl>
                    <div className="mt-1 grid grid-cols-1 gap-3 xl:grid-cols-2">
                      {/* PayPal option */}
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange(PaymentMethod.PAYPAL);
                          field.onBlur();
                        }}
                        className={cn(
                          "h-full w-full text-left flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors xl:px-4 xl:py-2.5",
                          field.value === PaymentMethod.PAYPAL
                            ? "border-2 border-blue-500 bg-blue-50"
                            : "border border-gray-200 bg-white hover:border-gray-300",
                        )}
                      >
                        <span className="mt-0.5 flex-shrink-0">
                          <span
                            className={cn(
                              "inline-flex h-4 w-4 items-center justify-center rounded-full border-2",
                              field.value === PaymentMethod.PAYPAL
                                ? "border-blue-500"
                                : "border-gray-400",
                            )}
                          >
                            {field.value === PaymentMethod.PAYPAL && (
                              <span className="h-2 w-2 rounded-full bg-blue-500" />
                            )}
                          </span>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-sm text-gray-900">
                              PayPal
                            </span>
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                              ★ Recommended
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500 leading-snug">
                            You're paid in full once we verify your miles
                            balance, before we book any flights. Same-day. 2.9%
                            PayPal fee.
                          </span>
                        </span>
                      </button>

                      {/* Wire / ACH option */}
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange(PaymentMethod.WIRE);
                          field.onBlur();
                        }}
                        className={cn(
                          "h-full w-full text-left flex items-start gap-3 rounded-md border px-3 py-2.5 transition-colors xl:px-4 xl:py-2.5",
                          field.value === PaymentMethod.WIRE
                            ? "border-2 border-blue-500 bg-blue-50"
                            : "border border-gray-200 bg-white hover:border-gray-300",
                        )}
                      >
                        <span className="mt-0.5 flex-shrink-0">
                          <span
                            className={cn(
                              "inline-flex h-4 w-4 items-center justify-center rounded-full border-2",
                              field.value === PaymentMethod.WIRE
                                ? "border-blue-500"
                                : "border-gray-400",
                            )}
                          >
                            {field.value === PaymentMethod.WIRE && (
                              <span className="h-2 w-2 rounded-full bg-blue-500" />
                            )}
                          </span>
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold text-sm text-gray-900">
                            Wire / ACH
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500 leading-snug">
                            No fees. Best for larger amounts. Paid after the
                            miles have been used (typically 2–6 weeks).
                          </span>
                        </span>
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedPaymentMethod === PaymentMethod.PAYPAL && (
              <div>
                <FormField
                  name="paypalEmail"
                  control={control}
                  render={({ field }) => {
                    const fieldError = errors.paypalEmail;
                    const fieldTouched = touchedFields.paypalEmail;

                    return (
                      <FormItem>
                        <FormLabel>Payment Details</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Enter your PayPal email address"
                              className={cn(
                                "h-9 text-sm border-gray-300 rounded-sm focus:border-blue-500 focus:ring-blue-500",
                                fieldError &&
                                  "border-red-500 focus:border-red-500 focus:ring-red-500",
                              )}
                              disabled={sameAsEmail}
                              {...field}
                            />
                            {!fieldError && field.value && fieldTouched && (
                              <Check className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                        {/* <div className="mt-2 flex items-center gap-2">
                          <Checkbox
                            id="sameAsEmail"
                            checked={sameAsEmail}
                            onCheckedChange={(checked) => {
                              const isChecked = Boolean(checked);
                              setSameAsEmail(isChecked);
                              if (isChecked) {
                                setValue("paypalEmail", accountEmail || "", {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              } else {
                                setValue("paypalEmail", "", {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                              }
                            }}
                          />
                          <Label
                            htmlFor="sameAsEmail"
                            className="text-sm hover:cursor-pointer"
                          >
                            Same as my account email
                          </Label>
                        </div> */}
                      </FormItem>
                    );
                  }}
                />
              </div>
            )}

            <SellCredentialsSection
              control={control}
              errors={errors}
              touchedFields={touchedFields}
              program={currentProgram}
            />

            <div className="mt-3 space-y-1">
              <PayoutConfidenceSection />

              <FormField
                name="agreedToTerms"
                control={control}
                render={({ field }) => (
                  <FormItem>
                    <TermsAndConditions
                      isOpen={isTermsAndConditionsOpen}
                      setIsOpen={setIsTermsAndConditionsOpen}
                      agreedToTerms={field.value}
                      setAgreedToTerms={(agreed) => {
                        field.onChange(agreed);
                      }}
                      formRef={formRef}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* <div className="mt-4 flex items-center gap-1 px-1">
              <Checkbox
                id="saveDetails"
                className="h-5 w-5"
                checked={saveDetails}
                onCheckedChange={(checked) =>
                  setSaveDetails(checked as boolean)
                }
              />
              <Label htmlFor="saveDetails" className="text-sm font-medium">
                Save my details for the quote
              </Label>
            </div> */}
            </div>

            <CheckoutButton
              isLastQuote={currentProgramIndex === programs.length - 1}
              disabled={isFormSubmitting}
              isFormSubmitting={isFormSubmitting}
              type="submit"
              transactionType={transactionType}
              payoutAmountLabel={formatPrice({
                price: currentProgram.totalPayout,
                fractionDigits: 0,
              })}
            />
            <p className="text-center text-xs text-gray-400 xl:hidden">
              Payout processed within 24 hours of verification
            </p>
          </form>
        </Form>
      </Card>
    </>
  );
}
